import { useMemo, useState } from "react";
import { Play, ClipboardText, CheckCircle, Stack } from "@phosphor-icons/react";
import ActiveWorkoutMode from "./ActiveWorkoutMode";
import { markWorkoutStart } from "@/lib/workoutSummary";

/**
 * "DA FARE OGGI" — suggests the next workout to run inside the client's
 * currently-active program.
 *
 * Logic (Mon=start of week):
 *  1. Pick the active program (status not "completata" and not future-pending).
 *  2. Walk its workouts in ascending `order`.
 *  3. The first workout WITHOUT any log dated within Mon..Sun of THIS week
 *     is the proposed one.
 *  4. If every workout already has a log this week → "Settimana completata".
 *  5. The weekly counter does not carry over: every Monday we restart from
 *     the first workout, regardless of what happened the previous week.
 */

function startOfThisWeek() {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dow = today.getDay(); // 0 = Sunday
  const monOffset = dow === 0 ? -6 : 1 - dow;
  const monday = new Date(today);
  monday.setDate(today.getDate() + monOffset);
  return monday;
}

function isWithinThisWeek(dateStr) {
  if (!dateStr) return false;
  const m = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return false;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  d.setHours(0, 0, 0, 0);
  const monday = startOfThisWeek();
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  return d >= monday && d <= sunday;
}

function pickActiveProgram(programs) {
  return programs.find((p) => {
    if (!p) return false;
    if (p.status === "completata") return false;
    const hasDates = !!(p.start_date && p.end_date);
    if (hasDates && p.status === "da_fare") return false;
    return true;
  }) || null;
}

export function TodoTodaySection({ programs, workouts, phases, logs, token, exerciseLibrary, onLogSaved, clientName }) {
  const [activeWorkout, setActiveWorkout] = useState(null);

  const { activeProgram, proposed, phase, allDone } = useMemo(() => {
    const program = pickActiveProgram(programs);
    if (!program) {
      return { activeProgram: null, proposed: null, phase: null, allDone: false };
    }
    const programWorkouts = workouts
      .filter((w) => w.program_id === program.id)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    if (programWorkouts.length === 0) {
      return { activeProgram: program, proposed: null, phase: null, allDone: false };
    }
    const completedThisWeek = new Set(
      logs.filter((l) => isWithinThisWeek(l.date)).map((l) => l.workout_id)
    );
    const next = programWorkouts.find((w) => !completedThisWeek.has(w.id)) || null;
    const ph = phases.find((p) => p.id === program.phase_id) || null;
    return {
      activeProgram: program,
      proposed: next,
      phase: ph,
      allDone: !next,
    };
  }, [programs, workouts, phases, logs]);

  if (!activeProgram) return null;

  return (
    <div className="mb-4" data-testid="todo-today-section">
      <h2 className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500 mb-2 px-1">
        Da fare oggi
      </h2>

      {allDone ? (
        <div
          className="bg-gradient-to-br from-[#34C759]/15 to-[#34C759]/5 border border-[#34C759]/40 rounded-2xl p-5 flex items-center gap-3"
          data-testid="todo-today-week-complete"
        >
          <div className="w-11 h-11 rounded-xl bg-[#34C759]/20 border border-[#34C759]/40 flex items-center justify-center flex-shrink-0">
            <CheckCircle size={22} weight="fill" className="text-[#34C759]" />
          </div>
          <div className="min-w-0">
            <p
              className="text-base font-bold tracking-tight text-white uppercase"
              style={{ fontFamily: "Barlow Condensed, sans-serif" }}
            >
              Settimana completata
            </p>
            <p className="text-[11px] text-zinc-400 mt-0.5">
              Hai chiuso tutti i workout della scheda di questa settimana 🔥
            </p>
          </div>
        </div>
      ) : proposed ? (
        <div
          className="bg-gradient-to-br from-[#007AFF]/25 to-[#007AFF]/10 border border-[#007AFF]/60 rounded-2xl p-5 space-y-3 shadow-[0_0_24px_rgba(0,122,255,0.18)]"
          data-testid="todo-today-card"
        >
          {activeProgram?.title && (
            <p
              className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#007AFF]"
              data-testid="todo-today-program-label"
            >
              {activeProgram.title}
            </p>
          )}
          <h3
            className="text-2xl font-black tracking-tight text-white"
            style={{ fontFamily: "Barlow Condensed, sans-serif" }}
            data-testid="todo-today-workout-name"
          >
            {proposed.title}
          </h3>
          <p className="text-[12px] text-zinc-300 flex items-center gap-1.5">
            <ClipboardText size={12} className="text-zinc-400" />
            {proposed.exercises?.length || 0} esercizi
          </p>
          <div className="border-t border-[#007AFF]/25 -mx-5" />
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-1.5 text-[11px] text-zinc-300" data-testid="todo-today-phase-label">
              <Stack size={11} className="text-zinc-400" />
              <span className="font-medium">{phase?.title || "—"}</span>
            </div>
            <button
              type="button"
              onClick={() => {
                markWorkoutStart(proposed.id);
                setActiveWorkout(proposed);
              }}
              className="flex items-center gap-1.5 px-4 py-2 rounded-xl border border-[#007AFF]/60 bg-[#007AFF]/20 hover:bg-[#007AFF]/35 text-white text-sm font-bold active:scale-95 transition-transform"
              data-testid="todo-today-start"
            >
              <Play size={12} weight="fill" /> Inizia
            </button>
          </div>
        </div>
      ) : (
        <div className="bg-[#0F0F0F] border border-[#1A1A1A] rounded-2xl p-4">
          <p className="text-[11px] text-zinc-500 italic">
            Nessuna scheda assegnata nel programma attivo.
          </p>
        </div>
      )}

      {activeWorkout && (
        <ActiveWorkoutMode
          workout={activeWorkout}
          token={token}
          logs={logs}
          exerciseLibrary={exerciseLibrary}
          onLogSaved={onLogSaved}
          onClose={() => setActiveWorkout(null)}
          clientName={clientName}
        />
      )}
    </div>
  );
}
